import _ from "lodash";

/**
 * Converts a string to lowercase while preserving TypeScript type information.
 * @template T - Type of the input string
 * @param input - String to be converted to lowercase
 * @returns Lowercase version of the input string with preserved type
 */
export function toLowercase<T extends string>(input: T): Lowercase<T> {
  // Workaround TS not doing this inference correctly
  return input.toLowerCase() as Lowercase<T>;
}

/**
 * Converts a string to title case while preserving TypeScript type information.
 * What is title case? An example: "The Quick Brown Fox Jumps over the Lazy Dog"
 *
 * @template T - Type of the input string
 * @param input - String to be converted to title case
 * @returns Title case version of the input string with preserved type
 */
export function toTitleCase<T extends string>(input: T): Capitalize<Lowercase<T>> {
  return _.capitalize(input) as Capitalize<Lowercase<T>>;
}

// https://stackoverflow.com/a/65642944
type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<SnakeToCamelCase<U>>}`
  : S;

/**
 * Converts a snake_case string to camelCase while keeping the type intact.
 */
export function snakeToCamelCase<T extends string>(input: T): SnakeToCamelCase<T> {
  const result = input.replace(/(_\w)/g, (m) => m[1].toUpperCase());
  return result as SnakeToCamelCase<T>;
}
